import { useMemo } from 'react';
import { Form, FormikProvider } from 'formik';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@antimatterhq/ui/components/dialog';
import { Button } from '@antimatterhq/ui/components/button';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import SourceFieldTab from './tabs/sourceFieldTab';
import AliasTab from './tabs/aliasTab';
import SqlTab from './tabs/sqlTab';
import LiteralTab from './tabs/literalTab';
import JoinTab from './tabs/joinTab';
import { useFormikWithAsyncValidation } from '@/hooks/form/useFormikWithAsyncValidation';
import { FormError, FormInput } from '@/components/form/form';
import { FormMultiValues } from '@/components/form/multiValues/FormMultiValues';
import {
  fieldSrcAliasTooltip,
  fieldSrcCopyTooltip,
  fieldSrcExprTooltip,
  fieldSrcJoinTooltip,
  fieldSrcStringTooltip,
} from '@/strings/StringsTooltips';
import {
  getConfigureFieldInitialValues,
  setConfigureFieldValues,
} from '@/lib/datasource';
import {
  DataSourceFieldSchema,
  DataSourceFieldSchemaValues,
} from '@/zod/schemas/dataSource/DataSourceField';
import { Datasource_FieldSpec } from '@/types/api';
import { AsyncValidationProvider } from '@/contexts/AsyncValidationContext';

const TABS: {
  value: DataSourceFieldSchemaValues['type'];
  label: string;
  info?: { content: string; interactive?: boolean };
}[] = [
  {
    value: 'source',
    label: 'Copy from',
    info: { content: fieldSrcCopyTooltip },
  },
  {
    value: 'alias',
    label: 'Alias of transformed field',
    info: { content: fieldSrcAliasTooltip },
  },
  {
    value: 'sql',
    label: 'SQL expression',
    info: { content: fieldSrcExprTooltip },
  },
  {
    value: 'literal',
    label: 'String literal',
    info: { content: fieldSrcStringTooltip },
  },
  { value: 'join', label: 'Join', info: { content: fieldSrcJoinTooltip } },
];

export type TabValue = (typeof TABS)[number]['value'];

interface CustomFieldDialogProps {
  onSubmit: (fieldSpec: Datasource_FieldSpec) => void;
  fieldSpec?: Datasource_FieldSpec;
  open: boolean;
  onClose: () => void;
  currentTableFields: string[];
  takenNames: string[];
  previousTable: string;
  currentTable: string;
  disableName?: boolean;
  hideAliasTab?: boolean;
}

function CustomFieldDialogContent({
  onSubmit,
  fieldSpec,
  open,
  onClose,
  takenNames,
  previousTable,
  currentTable,
  currentTableFields,
  disableName,
  hideAliasTab,
}: CustomFieldDialogProps) {
  const initialValues = useMemo(
    () => getConfigureFieldInitialValues(fieldSpec || {}, takenNames),
    [fieldSpec, takenNames],
  );
  const isEdit = Object.keys(fieldSpec || {}).length > 0;
  const { enableDatasourceExpectations } = useFlags<FlagSet>();

  const formik = useFormikWithAsyncValidation({
    initialValues,
    validate: toFormikValidate(DataSourceFieldSchema),
    validationSchema: toFormikValidationSchema(DataSourceFieldSchema),
    onSubmit: async (values, { resetForm }) => {
      const fields = setConfigureFieldValues(values);
      onSubmit(fields);
      onClose();
      resetForm();
    },
  });

  const { values, setFieldValue } = formik;
  const { type } = values;

  const handleTabChange = (value: DataSourceFieldSchemaValues['type']) => {
    setFieldValue('type', value);
  };

  const tabs = useMemo(
    () =>
      TABS.filter((q) => !hideAliasTab || q.value !== 'alias').map(
        (tab, index) => ({
          ...tab,
          ...(index === 0 && {
            label: `Copy from ${
              previousTable.length > 15
                ? previousTable.slice(0, 12) + '...'
                : previousTable
            }`,
          }),
        }),
      ),
    [previousTable, hideAliasTab],
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-[760px] bg-white dark:bg-grey-800"
        tabIndex={undefined}
        data-testid="custom-field-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {!isEdit && 'Add a field'}
            {isEdit && 'Edit field'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            How does the new field get its value?
          </DialogDescription>
        </DialogHeader>

        <FormikProvider value={formik}>
          <Form>
            <ButtonGroup
              options={tabs}
              value={values.type}
              onChange={(value) =>
                handleTabChange(value as DataSourceFieldSchemaValues['type'])
              }
              data-testid="field-type-button-group"
            />

            <Spacer size="lg" />

            <Label htmlFor="name" id="name-label" required>
              Field name
            </Label>
            <FormInput
              id="name"
              name="name"
              placeholder="Name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.name}
              aria-labelledby="name-label"
              disabled={disableName}
              data-testid="field-name-input"
            />
            <FormError name="name" />

            <Spacer size="lg" />

            <Label htmlFor="comment" id="comment-label">
              Comment for the field
            </Label>
            <FormInput
              id="comment"
              name="comment"
              placeholder="Enter a comment"
              onChange={formik.handleChange}
              value={formik.values.comment}
              aria-labelledby="comment-label"
              data-testid="field-description-input"
            />
            <FormError name="comment" />

            {enableDatasourceExpectations && (
              <>
                <Spacer size="lg" />

                <Label info="Each SQL expression will be evaluated and will raise an operational alert with the given message if it evaluates to anything other than True.">
                  Expectations
                </Label>

                <FormMultiValues
                  values={formik.values.assert ?? []}
                  name="assert"
                  onChange={formik.handleChange}
                  defaultValue={{ expr: '', message: '' }}
                  fields={[
                    { key: 'expr', placeholder: 'Expectation' },
                    { key: 'message', placeholder: 'Message' },
                  ]}
                  maxHeight={100}
                  data-testid="field-expectations-container"
                />
              </>
            )}

            {type === 'source' && (
              <SourceFieldTab previousTable={previousTable} />
            )}
            {type === 'alias' && (
              <AliasTab
                currentTable={currentTable}
                currentTableFields={currentTableFields}
              />
            )}
            {type === 'sql' && <SqlTab />}
            {type === 'literal' && <LiteralTab />}
            {type === 'join' && (
              <JoinTab
                currentTable={currentTable}
                currentTableFields={currentTableFields}
              />
            )}

            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="default"
                onClick={() => onClose()}
                data-testid="cancel-add-field-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={formik.submitDisabled}
                data-testid="custom-field-submit-button"
              >
                {!isEdit && 'Create field'}
                {isEdit && 'Apply changes'}
              </Button>
            </div>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomFieldDialog(props: CustomFieldDialogProps) {
  return (
    <AsyncValidationProvider>
      <CustomFieldDialogContent {...props} />
    </AsyncValidationProvider>
  );
}
